// FundEntryPrompt — modal that pops on Daily Entry mount when no fund-balance
// record exists for the selected (school, year, month). Lets the user quickly
// seed Opening Balance + Received Fund (class-wise) directly from the Daily
// Entry screen so cooking-cost ledgers stay accurate.
//
// Why this exists:
//   Schools used to land on Daily Entry, log meals, and only later realize
//   Fund Management was empty for that month — making closing balances
//   negative and Reports inaccurate. This nudge surfaces the missing step
//   inline, with a clear "Skip for now" escape hatch.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Save, X, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { fundApi } from "@/lib/setupApi";
import { NEG_MSG, noNegProps } from "@/lib/fundInput";
import { MONTH_LIST } from "@/lib/mealPlan";

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const CLASS_LABELS = {
  balVatika: "Bal Vatika",
  primary: "Primary",
  upperPrimary: "Upper Primary",
  highSchool: "High School",
};

function emptyClasswise() {
  return { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
}

// Per-session memory of "Skip for now" decisions so the dialog does not
// reopen every time the user changes the date dropdown.
const SKIPPED_KEYS = new Set();
const skipKey = (sid, y, m) => `${sid}|${y}|${m}`;

export default function FundEntryPrompt({ schoolId, year, month, onSaved, onYearChange, onMonthChange }) {
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [openingFund, setOpeningFund] = useState(emptyClasswise());
  const [receivedFund, setReceivedFund] = useState(emptyClasswise());
  const [openingRice, setOpeningRice] = useState(emptyClasswise());
  // Non-anchor months inherit the shared cascade opening (read-only here).
  const [autoOpening, setAutoOpening] = useState(false);
  const [receivedRice, setReceivedRice] = useState(emptyClasswise());
  const [saving, setSaving] = useState(false);

  // Year choices — current ± 2 years (mirrors FundManagement.jsx).
  const yearChoices = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur - 2, cur - 1, cur, cur + 1];
  }, []);

  // Fetch the fund-balance record for the active (school, year, month). If
  // missing → open the modal. If present → close silently (already done).
  useEffect(() => {
    if (!schoolId || !year || !month) return;
    if (SKIPPED_KEYS.has(skipKey(schoolId, year, month))) {
      setOpen(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rec = await fundApi.get(schoolId, year, month);
        if (cancelled) return;
        setExisting(rec);
        if (rec) {
          // Already saved → don't pester.
          setOpen(false);
          setOpeningFund({ ...emptyClasswise(), ...(rec.openingFund || {}) });
          setReceivedFund({ ...emptyClasswise(), ...(rec.receivedFund || {}) });
          setOpeningRice({ ...emptyClasswise(), ...(rec.openingRice || {}) });
          setReceivedRice({ ...emptyClasswise(), ...(rec.receivedRice || {}) });
        } else {
          const { cascadeOpening } = await import("@/lib/fundCascade");
          const op = await cascadeOpening(schoolId, year, month).catch(() => null);
          if (cancelled) return;
          setOpeningFund(op ? { ...emptyClasswise(), ...op.fund } : emptyClasswise());
          setReceivedFund(emptyClasswise());
          setOpeningRice(op ? { ...emptyClasswise(), ...op.rice } : emptyClasswise());
          setReceivedRice(emptyClasswise());
          setAutoOpening(!!op);
          setOpen(true);
        }
      } catch (err) {
        console.error("FundEntryPrompt fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, year, month]);

  // Keep the typed string so "0.125" and clearing work; coerce on save.
  const updateCell = (setter) => (k, v) => {
    if (String(v).trim().startsWith("-") || Number(v) < 0) { toast.error(NEG_MSG, { id: "fund-neg" }); return; }
    setter((s) => ({ ...s, [k]: v }));
  };
  const num = (o) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [k, Number(v) || 0]));
  const shown = (v) => (v === 0 ? "" : v ?? "");

  const skip = () => {
    SKIPPED_KEYS.add(skipKey(schoolId, year, month));
    setOpen(false);
  };

  const save = async () => {
    if (!schoolId) { toast.error("Pick an active school first"); return; }
    setSaving(true);
    try {
      await fundApi.upsert({
        schoolId, year, month,
        openingFund: num(openingFund), receivedFund: num(receivedFund),
        openingRice: num(openingRice), receivedRice: num(receivedRice),
        isLocked: false,
      });
      toast.success("Opening balance saved — you can now continue with Daily Entry");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!schoolId) return null;

  const monthLabel = MONTH_LIST.find((m) => m.value === Number(month))?.label || month;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && skip()}>
      <DialogContent
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto p-0"
        data-testid="fund-entry-prompt-dialog"
      >
        <div className="sticky top-0 z-10 border-b border-border bg-clay/10 px-5 pt-5 pb-4 sm:px-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-clay-dark">
              <Wallet className="h-5 w-5" />
              Fund Management — Opening Balance ସେଟ୍ କରନ୍ତୁ
            </DialogTitle>
            <DialogDescription className="text-ink-700">
              ଏହି ମାସର <b>Opening Balance</b> ଓ <b>Received Fund</b> ପ୍ରଥମେ ଭର୍ତ୍ତି କରନ୍ତୁ —
              ତାହାହେଲେ Daily Entry ର cooking cost ସଠିକ୍ track ହେବ। Phone-style quick entry, classwise।
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-4 px-5 py-4 sm:px-6">
          {/* Year + Month selectors so user can re-target a different period */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700">Year</label>
              <Select value={String(year)} onValueChange={(v) => onYearChange?.(Number(v))}>
                <SelectTrigger className="h-9 text-xs" data-testid="fund-prompt-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearChoices.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700">Month</label>
              <Select value={String(month)} onValueChange={(v) => onMonthChange?.(Number(v))}>
                <SelectTrigger className="h-9 text-xs" data-testid="fund-prompt-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_LIST.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-clay/30 bg-sand-50 p-3">
            <p className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-clay">
              {monthLabel} {year} — Cooking Cost / Fund (₹)
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CLASS_KEYS.map((k) => (
                <div key={`fund-${k}`} className="rounded-md border border-border bg-white p-2">
                  <p className="text-[0.65rem] font-semibold text-ink-700">{CLASS_LABELS[k]}</p>
                  <div className="mt-1 grid grid-cols-2 gap-1.5">
                    <Input
                      type="number" min="0" step="0.01"
                      placeholder="Opening"
                      value={shown(openingFund[k])} {...noNegProps}
                      readOnly={autoOpening}
                      onChange={(e) => updateCell(setOpeningFund)(k, e.target.value)}
                      className="h-8 text-xs"
                      data-testid={`fund-prompt-opening-fund-${k}`}
                    />
                    <Input
                      type="number" min="0" step="0.01"
                      placeholder="Received"
                      value={shown(receivedFund[k])} {...noNegProps}
                      onChange={(e) => updateCell(setReceivedFund)(k, e.target.value)}
                      className="h-8 text-xs"
                      data-testid={`fund-prompt-received-fund-${k}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-moss/30 bg-moss/5 p-3">
            <p className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-moss-dark">
              {monthLabel} {year} — Rice (kg)
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CLASS_KEYS.map((k) => (
                <div key={`rice-${k}`} className="rounded-md border border-border bg-white p-2">
                  <p className="text-[0.65rem] font-semibold text-ink-700">{CLASS_LABELS[k]}</p>
                  <div className="mt-1 grid grid-cols-2 gap-1.5">
                    <Input
                      type="number" min="0" step="0.001"
                      placeholder="Opening"
                      value={shown(openingRice[k])} {...noNegProps}
                      readOnly={autoOpening}
                      onChange={(e) => updateCell(setOpeningRice)(k, e.target.value)}
                      className="h-8 text-xs"
                      data-testid={`fund-prompt-opening-rice-${k}`}
                    />
                    <Input
                      type="number" min="0" step="0.001"
                      placeholder="Received"
                      value={shown(receivedRice[k])} {...noNegProps}
                      onChange={(e) => updateCell(setReceivedRice)(k, e.target.value)}
                      className="h-8 text-xs"
                      data-testid={`fund-prompt-received-rice-${k}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[0.7rem] text-ink-700">
            🛈 ଆପଣ <Link to="/funds" className="font-semibold text-clay underline">Fund Management</Link> tab କୁ ଯାଇ ଏହାକୁ ଆପଡେଟ୍ କରିପାରିବେ।
          </p>
        </div>

        <DialogFooter className="gap-2 border-t border-border bg-sand-50 px-5 py-3 sm:px-6">
          <Button variant="outline" size="sm" onClick={skip} data-testid="fund-prompt-skip">
            <X className="mr-1 h-3.5 w-3.5" /> Skip for now
          </Button>
          <Link to="/funds" className="inline-flex">
            <Button variant="outline" size="sm" data-testid="fund-prompt-goto-full">
              Open full Fund Management <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </Link>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-clay text-white hover:bg-clay-dark"
            data-testid="fund-prompt-save"
          >
            <Save className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
